import React, { useState, useEffect, useCallback } from 'react';
import { Service } from '../types/booking.types';
import { apiCall } from '../../../../utils/apiClient';
import CreateJobModal from './CreateJobModal';

interface JobItem {
  id: number;
  serviceId: number;
  serviceName: string;
  quantity: number | string;
  unit: string;
  rate?: number | string;
  amount?: number | string;
  addonIds: number[];
  addonNames: string[];
  addonItems?: {
    id: number | string;
    title: string;
    quantity: number | string;
    unit: string;
    rate?: number | string;
    amount?: number | string;
  }[];
}

interface Job {
  id: number;
  jobNumber: string;
  jobTitle: string;
  jobItems: JobItem[];
  partnerId?: number;
  partnerName?: string;
  partnerIds?: number[];
  partnerNames?: string[];
  partnerStatus: 'not-assigned' | 'pending-acceptance' | 'accepted' | 'rejected';
  status: 'pending' | 'assigned' | 'in-progress' | 'completed' | 'cancelled';
  scheduledDate: string;
  completedDate?: string;
  notes?: string;
  totalPartnerAmount?: number | string;
}

interface JobManagementTabProps {
  bookingId: number;
  services: Service[];
  additionalServices?: Service[];
}

const hasDefinedValue = (value: unknown): boolean => value !== null && value !== undefined && value !== '';

const JobManagementTab: React.FC<JobManagementTabProps> = ({ bookingId, services, additionalServices = [] }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newJob, setNewJob] = useState({
    jobTitle: '',
    scheduledDate: '',
    notes: '',
  });
  const [selectedServices, setSelectedServices] = useState<number[]>([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const [jobsError, setJobsError] = useState<string | null>(null);
  const [servicesWithPartnerPricing, setServicesWithPartnerPricing] = useState<Service[]>([]);

  // Combine all services
  const allServices = [...services, ...additionalServices];

  // Initialize with empty jobs - will be populated by API
  const [jobs, setJobs] = useState<Job[]>([]);

  // Fetch jobs from API on component mount
  const fetchJobs = useCallback(async () => {
    setIsLoadingJobs(true);
    setJobsError(null);
    try {
      const response = await apiCall({
        endpoint: `partner-jobs/booking/${bookingId}`,
        method: 'GET',
      });

      if (response.status === 200 || response.status === 201) {
        const jobsData = Array.isArray(response.data) ? response.data : response.data?.jobs || [];

        // Map API response to Job format
        const mappedJobs: Job[] = jobsData.map((apiJob: any) => {
          const items = Array.isArray(apiJob.items) ? apiJob.items : [];
          const parentItems = items.filter((item: any) => !item.parent_item_id);

          const jobItems: JobItem[] = parentItems.map((item: any) => {
            const childItems = items.filter((child: any) => child.parent_item_id === item.id);
            return {
              id: Number(item.id) || item.id,
              serviceId: Number(item.source_id) || item.source_id,
              serviceName: item.title,
              quantity: item.quantity,
              unit: item.unit,
              rate: item.rate,
              amount: item.amount,
              addonIds: childItems.map((child: any) => Number(child.source_id ?? child.id) || child.source_id || child.id),
              addonNames: childItems.map((child: any) => child.title || child.name),
              addonItems: childItems.map((child: any) => ({
                id: child.id,
                title: child.title || child.name,
                quantity: child.quantity,
                unit: child.unit,
                rate: child.rate,
                amount: child.amount,
              })),
            };
          });

          const partnerIds = Array.isArray(apiJob.partner_ids)
            ? apiJob.partner_ids.map((id: any) => Number(id))
            : undefined;
          const partnerNames = Array.isArray(apiJob.partner_names) ? apiJob.partner_names : undefined;

          return {
            id: Number(apiJob.id) || apiJob.id,
            jobNumber: apiJob.job_id,
            jobTitle: apiJob.title,
            jobItems,
            partnerId: apiJob.partner_id ? Number(apiJob.partner_id) : undefined,
            partnerName: apiJob.partner_name,
            partnerIds,
            partnerNames,
            partnerStatus: apiJob.partner_status || 'not-assigned',
            status: apiJob.status || 'pending',
            scheduledDate: apiJob.estimated_start_date || apiJob.created_at,
            completedDate: apiJob.completed_date,
            notes: apiJob.notes,
            totalPartnerAmount: apiJob.total_partner_amount,
          };
        });

        setJobs(mappedJobs);
      } else {
        setJobsError(response.message || 'Failed to fetch jobs');
      }
    } catch (err: any) {
      setJobsError(err.message || 'An error occurred while fetching jobs');
      console.error('Fetch jobs error:', err);
    } finally {
      setIsLoadingJobs(false);
    }
  }, [bookingId]);

  useEffect(() => {
    if (bookingId) {
      fetchJobs();
    }
  }, [bookingId, fetchJobs]);

  useEffect(() => {
    let cancelled = false;

    const findMatchingAddon = (addons: any[], addon: any) => {
      const addonId = addon?.addon_id ?? addon?.id;
      const addonName = String(addon?.addon_name ?? addon?.name ?? '').trim().toLowerCase();

      return addons.find((item: any) => {
        const masterAddonId = item?.id;
        const masterAddonName = String(item?.name ?? item?.addon_name ?? '').trim().toLowerCase();

        if (hasDefinedValue(addonId) && hasDefinedValue(masterAddonId)) {
          return String(masterAddonId) === String(addonId);
        }

        return addonName && masterAddonName && addonName === masterAddonName;
      });
    };

    const hydratePartnerPricing = async () => {
      const candidates = allServices.filter((service: any) => {
        const missingServicePartnerPrice = !hasDefinedValue(service?.partner_price);
        const missingAddonPartnerPrice = (service?.addons || []).some(
          (addon: any) => !hasDefinedValue(addon?.partner_price)
        );

        return hasDefinedValue(service?.service_id) && (missingServicePartnerPrice || missingAddonPartnerPrice);
      });

      if (candidates.length === 0) {
        setServicesWithPartnerPricing(allServices);
        return;
      }

      try {
        const detailEntries = await Promise.all(
          candidates.map(async (service: any) => {
            const response = await apiCall({
              endpoint: `services/${service.service_id}`,
              method: 'GET',
            });

            if (response?.status === 200 && response.data) {
              return [String(service.id), response.data] as const;
            }

            return null;
          })
        );

        if (cancelled) return;

        const detailMap = new Map(
          detailEntries.filter(Boolean) as readonly (readonly [string, any])[]
        );

        const hydratedServices = allServices.map((service: any) => {
          const detail = detailMap.get(String(service.id));
          if (!detail) return service;

          const detailAddons = Array.isArray(detail?.addons) ? detail.addons : [];

          return {
            ...service,
            partner_price: hasDefinedValue(service?.partner_price)
              ? service.partner_price
              : detail?.partner_price ?? service?.partner_price,
            with_material: hasDefinedValue(service?.with_material)
              ? service.with_material
              : detail?.with_material ?? service?.with_material,
            addons: (service?.addons || []).map((addon: any) => {
              const detailAddon = findMatchingAddon(detailAddons, addon);
              if (!detailAddon) return addon;

              return {
                ...addon,
                partner_price: hasDefinedValue(addon?.partner_price)
                  ? addon.partner_price
                  : detailAddon?.partner_price ?? addon?.partner_price,
                with_material: hasDefinedValue(addon?.with_material)
                  ? addon.with_material
                  : detailAddon?.with_material ?? addon?.with_material,
              };
            }),
          };
        });

        setServicesWithPartnerPricing(hydratedServices);
      } catch (err) {
        console.error('Failed to hydrate partner pricing for job management:', err);
        if (!cancelled) {
          setServicesWithPartnerPricing(allServices);
        }
      }
    };

    hydratePartnerPricing();

    return () => {
      cancelled = true;
    };
  }, [services, additionalServices]);

  const toNumber = (value: unknown, fallback = 0): number => {
    if (value === null || value === undefined || value === '') return fallback;
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const toBool = (value: unknown): boolean => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') return value === '1' || value.toLowerCase() === 'true';
    return false;
  };

  const getPartnerStatusColor = (status: Job['partnerStatus']) => {
    switch (status) {
      case 'accepted':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'pending-acceptance':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'rejected':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'not-assigned':
        return 'bg-gray-100 text-gray-700 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusColor = (status: Job['status']) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'in-progress':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'assigned':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'cancelled':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const handleOpenCreateModal = () => {
    setShowCreateModal(true);
    setSelectedServices([]);
  };

  const toggleServiceSelection = (serviceId: number) => {
    setSelectedServices((prev) =>
      prev.includes(serviceId) ? prev.filter((id) => id !== serviceId) : [...prev, serviceId]
    );
  };

  const handleCreateJob = (createdJobData?: any) => {
    if (!createdJobData) {
      alert('Job created, but response is missing. Please refresh.');
      return;
    }

    setShowCreateModal(false);
    setNewJob({ jobTitle: '', scheduledDate: '', notes: '' });
    setSelectedServices([]);
    fetchJobs();
  };

  // Get services that are not yet in any job
  const usedServiceIds = jobs.flatMap((job) => job.jobItems.map((item) => item.serviceId));
  const availableServices = servicesWithPartnerPricing.filter((service) => !usedServiceIds.includes(service.id));

  // Calculate total job cost based on partner prices
  const calculateJobTotal = (job: Job): number => {
    return job.jobItems.reduce((total, item) => {
      const service = servicesWithPartnerPricing.find((s) => s.id === item.serviceId);
      const serviceRate = toNumber(item.rate ?? service?.partner_price);
      const serviceQty = toNumber(item.quantity);
      const serviceAmount = toNumber(item.amount, serviceRate * serviceQty);

      const addonsCostFromItems = (item.addonItems || []).reduce((addonTotal, addonItem) => {
        const addonRate = toNumber(addonItem.rate);
        const addonQty = toNumber(addonItem.quantity, 1);
        const addonAmount = toNumber(addonItem.amount, addonRate * addonQty);
        return addonTotal + addonAmount;
      }, 0);

      const addonsCostFromServices = item.addonIds.reduce((addonTotal, addonId) => {
        const addon = service?.addons?.find((a) => a.id === addonId);
        if (!addon) return addonTotal;
        return addonTotal + toNumber(addon.partner_price) * toNumber(addon.quantity, 1);
      }, 0);

      const addonsCost = (item.addonItems && item.addonItems.length > 0) ? addonsCostFromItems : addonsCostFromServices;

      return total + serviceAmount + addonsCost;
    }, 0);
  };

  return (
    <div className="mb-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold text-gray-800 flex items-center">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
          Job Management
          <span className="ml-3 text-sm font-normal text-gray-500">({jobs.length} Jobs)</span>
        </h3>
        <button
          onClick={handleOpenCreateModal}
          disabled={availableServices.length === 0}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-all text-sm font-medium flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Create New Job
        </button>
      </div>

      {availableServices.length === 0 && jobs.length > 0 && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">
          ✓ All booked services have been assigned to jobs
        </div>
      )}

      {/* Jobs Table */}
      {jobs.length > 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Job Number
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Job Title
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Services
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Partner
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Scheduled Date
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {jobs.map((job) => (
                  <tr key={job.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <p className="text-sm font-medium text-gray-900">{job.jobNumber}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-gray-900">{job.jobTitle}</p>
                      {job.notes && <p className="text-xs text-gray-500 mt-1 line-clamp-1">{job.notes}</p>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-sm text-gray-700">
                        {job.jobItems.length} Service{job.jobItems.length > 1 ? 's' : ''}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {job.partnerName || (job.partnerNames && job.partnerNames.length > 0) || (job.partnerIds && job.partnerIds.length > 0) ? (
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {job.partnerName
                              ? job.partnerName
                              : job.partnerNames && job.partnerNames.length > 1
                              ? `Multiple partners (${job.partnerNames.length})`
                              : job.partnerNames?.[0]
                              ? job.partnerNames?.[0]
                              : job.partnerIds && job.partnerIds.length > 1
                              ? `Multiple partners (${job.partnerIds.length})`
                              : job.partnerIds?.[0]}
                          </p>
                          <span
                            className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${getPartnerStatusColor(
                              job.partnerStatus
                            )}`}
                          >
                            {job.partnerStatus === 'not-assigned'
                              ? 'No Partner'
                              : job.partnerStatus === 'pending-acceptance'
                              ? 'Pending'
                              : job.partnerStatus === 'accepted'
                              ? 'Accepted'
                              : 'Rejected'}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-orange-600 font-medium">Not Assigned</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                          job.status
                        )}`}
                      >
                        {job.status.replace('-', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <p className="text-sm text-gray-700">{new Date(job.scheduledDate).toLocaleDateString()}</p>
                      {job.completedDate && (
                        <p className="text-xs text-gray-500 mt-1">
                          Completed: {new Date(job.completedDate).toLocaleDateString()}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <p className="text-sm font-semibold text-gray-900">
                        ₹{(toNumber(job.totalPartnerAmount, calculateJobTotal(job))).toLocaleString('en-IN')}
                      </p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : isLoadingJobs ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <div className="inline-block">
            <div className="w-8 h-8 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
          </div>
          <p className="text-gray-600 mt-4">Loading jobs...</p>
        </div>
      ) : jobsError ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700 text-sm"><strong>Error:</strong> {jobsError}</p>
        </div>
      ) : (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
            />
          </svg>
          <h4 className="text-lg font-semibold text-gray-700 mb-2">No Jobs Created Yet</h4>
          <p className="text-gray-500 mb-4">Create jobs from your booked services to assign to partners</p>
        </div>
      )}

      <CreateJobModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreateJob}
        availableServices={availableServices}
        selectedServices={selectedServices}
        toggleServiceSelection={toggleServiceSelection}
        newJob={newJob}
        setNewJob={setNewJob}
        bookingId={bookingId}
      />

    </div>
  );
};

export default JobManagementTab;
